import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import {
  getChildProperties,
  getRequiredFields,
  getSchemaNodesFromUnion,
} from "src/jsonSchema/schemaUtils";
import {
  isArray,
  isArrayOfRef,
  isArrayOfUnion,
  isArrayWithItems,
  isNullable,
  isObjectWithProperties,
  isPrimitive,
  isPureUnion,
  isTupleArray,
  isUnion,
  JsonSchema,
  JsonSchemaNode,
} from "src/jsonSchema/types";
import { JSONSchemaViewerDisplayOptions } from "src/jsonSchema/viewer/JSONSchemaRenderer";
import {
  asArrayLabel,
  generateDataPath,
  getArrayItemTypeLabel,
  getTypeIcon,
} from "src/jsonSchema/viewer/renderUtils";

export type UnionChoice = {
  id: string;
  typeLabel: string;
  typeIcon: IconDefinition;
  schemaNode: JsonSchemaNode; // the actual choice node
  node?: ViewModelNode; // only present if expandable
};

export type ViewModelNodeType = "property" | "choice-only";

export type ViewModelNode = {
  id: string;
  nodeType: ViewModelNodeType;
  name: string; // property name (rendered as the badge)
  dataPath: string; // e.g., "data.user.name"
  typeLabel: string; // e.g., "string", "object", "User"
  icon?: IconDefinition;

  // Type information
  isNullable: boolean;
  isRequired: boolean;
  isRef: boolean;

  // Tree structure
  children: ViewModelNode[];
  parent?: ViewModelNode;
  depth: number;

  // Union types are converted into choice descriptors
  unionChoices: UnionChoice[];

  // Auto-expand state (can be overridden by user interaction)
  initiallyExpanded: boolean;

  // Original property for reference
  property: JsonSchemaNode;
};

type ViewModelNodeDescription = Omit<
  ViewModelNode,
  // excluding properties set during traversal
  | "id"
  | "name"
  | "dataPath"
  | "property"
  | "parent"
  | "depth"
  | "children"
  | "unionChoices"
  | "isRequired"
>;

export type ViewModelTree = {
  root: ViewModelNode;
};

type BuildContext = {
  schema: JsonSchema;
  displayOptions: JSONSchemaViewerDisplayOptions;
  idCounter: number;
  maxDepth: number; // Maximum allowed depth
};

type BuildNodeParams = {
  property: JsonSchemaNode;
  name: string;
  dataPath: string;
  depth: number;
  parent?: ViewModelNode; // only undefined for root
  isRequired: boolean;
  context: BuildContext;
  isChoiceOnly?: boolean;
};

const makeNodeDescription = (
  property: JsonSchemaNode,
): ViewModelNodeDescription => {
  if (property.$ref) {
    return {
      ...baseDescription(property, property.$ref, getTypeIcon("object")),
      isRef: true,
    };
  }

  if (isUnion(property)) {
    const unionType = "anyOf" in property ? "anyOf" : "oneOf";
    return baseDescription(property, unionType, getTypeIcon("union"));
  }

  if (isArray(property)) {
    const typeLabel = getArrayItemTypeLabel(property);

    return {
      ...baseDescription(property, typeLabel, getTypeIcon("array")),
      isRef: isArrayOfRef(property),
    };
  }

  if (isObjectWithProperties(property)) {
    return baseDescription(property, "object", getTypeIcon("object"));
  }

  if ("enum" in property) {
    return baseDescription(property, "enum", getTypeIcon("enum"));
  }

  // primitives or mixed types
  const type = Array.isArray(property.type)
    ? property.type.filter((t) => t !== "null").join(" | ")
    : (property.type ?? "unknown");

  return baseDescription(property, type, getTypeIcon(type));
};

const baseDescription = (
  property: JsonSchemaNode,
  typeLabel: string,
  icon: IconDefinition,
): ViewModelNodeDescription => ({
  nodeType: "property",
  typeLabel,
  icon,
  isNullable: isNullable(property),
  isRef: false,
  initiallyExpanded: false, // final expansion decided later
});

const choiceOnlyDescription = (): ViewModelNodeDescription => ({
  nodeType: "choice-only",
  typeLabel: "choice",
  icon: getTypeIcon("union"),
  isNullable: false,
  isRef: false,
  initiallyExpanded: true,
});

const sentinelDescription = (): ViewModelNodeDescription => ({
  nodeType: "property",
  typeLabel: "...",
  icon: getTypeIcon("string"),
  isNullable: false,
  isRef: false,
  initiallyExpanded: false,
});

export const buildViewModelTree = (
  schema: JsonSchema,
  displayOptions: JSONSchemaViewerDisplayOptions,
): ViewModelTree => {
  const context: BuildContext = {
    schema,
    displayOptions,
    idCounter: 0,
    maxDepth: displayOptions.maxDepth,
  };
  const root = buildViewModelNode({
    property: schema,
    name: "",
    dataPath: displayOptions.pathPrefix,
    depth: 0,
    parent: undefined,
    isRequired: false,
    context,
  });
  return { root };
};

const buildViewModelNode = ({
  property,
  name,
  dataPath,
  depth,
  parent,
  isRequired,
  context,
  isChoiceOnly = false,
}: BuildNodeParams): ViewModelNode => {
  const id = `node_${context.idCounter++}`;
  const traversalProps = {
    id,
    name,
    dataPath,
    depth,
    parent,
    property,
    isRequired,
  };

  // Sentinel node when reaching maxDepth
  if (depth >= context.maxDepth) {
    return {
      ...sentinelDescription(),
      ...traversalProps,
      children: [],
      unionChoices: [],
    };
  }

  const desc = isChoiceOnly
    ? choiceOnlyDescription()
    : makeNodeDescription(property);

  const shouldAutoExpand =
    desc.nodeType === "choice-only" ||
    (context.displayOptions.autoExpandDepth > 0 &&
      depth <= context.displayOptions.autoExpandDepth);

  const node: ViewModelNode = {
    ...desc,
    ...traversalProps,
    initiallyExpanded: shouldAutoExpand,
    children: [],
    unionChoices: [],
  };

  node.unionChoices = buildChoices(property, node, context);
  node.children = buildChildren(property, node, context);

  return node;
};

const buildChildren = (
  property: JsonSchemaNode,
  node: ViewModelNode,
  context: BuildContext,
): ViewModelNode[] => {
  const childProperties = getChildProperties(property, context.schema);
  if (childProperties == null) {
    return [];
  }

  const requiredFields = getRequiredFields(property, context.schema);
  const propertyNames = Object.keys(childProperties);
  return propertyNames.map((propName) => {
    const childProperty = childProperties[propName];
    const childDataPath = generateDataPath(node.dataPath, propName, property);
    const childIsRequired = requiredFields.includes(propName);
    return buildViewModelNode({
      property: childProperty,
      name: propName,
      dataPath: childDataPath,
      depth: node.depth + 1,
      parent: node,
      isRequired: childIsRequired,
      context,
    });
  });
};

const buildChoices = (
  property: JsonSchemaNode,
  parentNode: ViewModelNode,
  context: BuildContext,
): UnionChoice[] => {
  // Only unions or arrays-of-unions produce choices
  if (!isUnion(property) && !isArrayOfUnion(property)) {
    return [];
  }

  const choiceSchemas = isArrayOfUnion(property)
    ? getSchemaNodesFromUnion(property.items)
    : getSchemaNodesFromUnion(property);

  return choiceSchemas.map((choiceSchema, index): UnionChoice => {
    const choiceId = `${context.idCounter++}_choice_${index}`;
    const typeLabel = isArrayOfUnion(property)
      ? asArrayLabel(makeNodeDescription(choiceSchema).typeLabel)
      : makeNodeDescription(choiceSchema).typeLabel;
    const typeIcon = getTypeIcon(typeLabel);

    const choice: UnionChoice = {
      id: choiceId,
      typeLabel,
      typeIcon,
      schemaNode: choiceSchema,
    };

    // Only attach a node if this schema is expandable
    if (shouldAttachNodeForChoice(choiceSchema)) {
      choice.node = buildViewModelNode({
        property: choiceSchema,
        name: parentNode.name,
        dataPath: isArrayOfUnion(property)
          ? `${parentNode.dataPath}[]`
          : parentNode.dataPath,
        depth: parentNode.depth + 1,
        parent: parentNode,
        isRequired: parentNode.isRequired,
        context,
        isChoiceOnly: isChoiceOnlySchemaNode(choiceSchema),
      });
    }

    return choice;
  });
};

/**
 * A schema node should be represented as a choice-only tree node when:
 * 1. It's a pure union (no type or properties of its own) and
 * 2. ALL its children are non-expandable (primitives or pure nested unions)
 */
const isChoiceOnlySchemaNode = (property: JsonSchemaNode): boolean => {
  if (!isPureUnion(property)) {
    return false;
  }
  // Check if all direct children are non-expandable
  return getSchemaNodesFromUnion(property).every((childProperty) => {
    if (isPrimitive(childProperty)) return true;
    if (childProperty.$ref) return false;
    if (isObjectWithProperties(childProperty)) return false;
    if (isArrayWithItems(childProperty)) return false;
    // Nested unions are considered non-expandable
    return isUnion(childProperty);
  });
};

/**
 * Only attach ViewModelNodes to choices that can be expanded further
 */
const shouldAttachNodeForChoice = (property: JsonSchemaNode): boolean => {
  if (property == null) return false;
  if (property.$ref) return true;
  if (isObjectWithProperties(property)) return true;
  if (isArrayWithItems(property) && !isTupleArray(property)) {
    // Array type might be expandable if it has expandable items
    return shouldAttachNodeForChoice(property.items as JsonSchemaNode);
  }
  return isUnion(property);
};
